import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  Snackbar
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useAuth } from '../../App'
import { useTranslation } from 'react-i18next'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

interface UserOption {
  id: number
  email: string
  label: string
}

const CreditsTab: React.FC = () => {
  const { t } = useTranslation()
  const auth = useAuth()
  const [users, setUsers] = useState<UserOption[]>([])
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [amount, setAmount] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [fetchingUsers, setFetchingUsers] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const fetchUsers = async () => {
    setFetchingUsers(true)
    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/admin/billing/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }
      const data = await response.json()
      setUsers(data.map((u: { id: number; email: string }) => ({
        id: u.id,
        email: u.email,
        label: `${u.email} (ID: ${u.id})`
      })))
    } catch (err) {
      setError('Failed to load users')
      console.error('Fetch users error:', err)
    } finally {
      setFetchingUsers(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleAddCredit = async () => {
    if (!selectedUser) {
      setError('Please select a user')
      return
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const token = await auth.getToken()
      const response = await fetch(`${API_BASE}/wallet/admin/credit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          amountEuros: parseFloat(amount),
          reason: reason || 'Admin credit'
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to add credit')
      }

      const result = await response.json()
      setSuccess(`Successfully added ${amount} EUR to ${selectedUser.email}. New balance: ${result.newBalanceEuros.toFixed(2)} EUR`)

      // Reset form
      setAmount('')
      setReason('')
      setSelectedUser(null)
    } catch (err: any) {
      setError(err.message || 'Failed to add credit')
      console.error('Add credit error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('addWalletCredits')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('creditsDescription')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={users}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                loading={fetchingUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('selectUser')}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {fetchingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`${t('amount')} (EUR)`}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('reason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('creditReasonPlaceholder')}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                onClick={handleAddCredit}
                disabled={loading || !selectedUser || !amount}
                size="large"
              >
                {loading ? t('adding') : t('addCredit')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CreditsTab
